<script setup>
  import { ref, computed, watch } from "vue";
  import { useToastsStore } from "@/stores/toastsStore";

  import { Plus, Trash2 } from "lucide-vue-next";

  import FormDialog from "@/components/episto-ui/form-dialog/FormDialog.vue";
  import FormField from "@/components/episto-ui/form-dialog/FormField.vue";

  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";

  import { handleTabKey } from "@composables/useTabIndent";
  import { useSqlQueryStore } from "@stores/sqlQuery";

  const props = defineProps({
    open: { type: Boolean, required: true },
    template: { type: Object, default: null },
    currentSql: { type: String, default: "" },
  });

  const toast = useToastsStore();
  const emit = defineEmits(["update:open", "saved"]);

  const sqlQueryStore = useSqlQueryStore();

  const name = ref("");
  const description = ref("");
  const sql = ref("");
  const params = ref([]);

  const isEditing = computed(() => !!props.template);

  const canSave = computed(
    () =>
      name.value.trim() && sql.value.trim() && params.value.every((p) => p.name.trim() && p.type.trim()),
  );

  watch(
    () => props.open,
    (isOpen) => {
      if (!isOpen) return;
      if (props.template) {
        name.value = props.template.name;
        description.value = props.template.description || "";
        sql.value = props.template.sql_query;
        params.value = (props.template.params || []).map((p) => ({ ...p }));
      } else {
        name.value = "";
        description.value = "";
        sql.value = props.currentSql;
        params.value = [];
      }
    },
  );

  function addParam() {
    params.value.push({ name: "", label: "", type: "text" });
  }

  function removeParam(index) {
    params.value.splice(index, 1);
  }

  async function save() {
    const payload = {
      name: name.value,
      description: description.value,
      sql_query: sql.value,
      params: params.value,
    };

    try {
      if (isEditing.value) {
        const updated = await sqlQueryStore.updateTemplate(props.template.id, payload);
        emit("update:open", false);
        emit("saved", updated);
        toast.success("Template updated");
      } else {
        const created = await sqlQueryStore.createTemplate(payload);
        emit("update:open", false);
        emit("saved", created);
        toast.success("Template saved");
      }
    } catch (error) {
      toast.error(error.message || "Failed to save template");
    }
  }

  const paramTypeOptions = [
    { label: "Text", value: "text" },
    { label: "Number", value: "number" },
    { label: "Date", value: "date" },
    { label: "Survey select", value: "survey_select" },
    { label: "Survey multi-select", value: "survey_multiselect" },
    { label: "Campaign select", value: "campaign_select" },
    { label: "Account select", value: "account_select" },
  ];
</script>

<template>
  <FormDialog
    :open="open"
    :is-editing="isEditing"
    entity-name="Template"
    max-width="600px"
    :can-save="canSave"
    @update:open="emit('update:open', $event)"
    @save="save">
    <FormField
      id="template-name"
      label="Name">
      <Input
        id="template-name"
        v-model="name"
        placeholder="Template name" />
    </FormField>

    <FormField
      id="template-description"
      label="Description">
      <Textarea
        id="template-description"
        v-model="description"
        placeholder="Optional description" />
    </FormField>

    <div class="space-y-3">
      <Label class="text-sm font-medium">Parameters</Label>

      <div
        v-if="params.length"
        class="flex items-center gap-2 text-xs text-muted-foreground">
        <div class="flex-1">Name</div>
        <div class="flex-1">Label</div>
        <div class="flex-1">Type</div>
        <div class="w-9" />
      </div>

      <div
        v-for="(param, index) in params"
        :key="index"
        class="flex items-center gap-2">
        <div class="flex-1">
          <Input v-model="param.name" />
        </div>
        <div class="flex-1">
          <Input v-model="param.label" />
        </div>
        <div class="flex-1">
          <Select
            :model-value="param.type"
            :options="paramTypeOptions"
            @update:model-value="param.type = $event" />
        </div>
        <Button
          variant="ghost"
          size="icon"
          class="shrink-0"
          @click="removeParam(index)">
          <Trash2 class="h-4 w-4" />
        </Button>
      </div>
      <Button
        variant="outline"
        size="sm"
        @click="addParam">
        <Plus class="h-4 w-4 mr-1" />
        Add parameter
      </Button>
    </div>

    <FormField
      id="template-sql"
      label="SQL Query">
      <Textarea
        id="template-sql"
        v-model="sql"
        class="font-mono text-sm min-h-[200px]"
        placeholder="SELECT ..."
        @keydown.tab="handleTabKey" />
    </FormField>
  </FormDialog>
</template>
